/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Gold standard depth — follows the same structure as the estimates page.
 * Grounded in docs/agent-context/subsystems/blueprint-takeoffs.md.
 *
 * AI claims intentionally minimal — blueprint AI detection is not
 * production-trustworthy yet. Do NOT market AI features on this page
 * without confirming accuracy with Matthew. That includes:
 *   - Blueprint vision analysis / element detection
 *   - AI cost matching / confidence-scored matching
 *   - Any "Matching Wizard" UI — that component does not exist.
 * Cost matching happens under the hood during Populate Builder but is
 * not a user-facing feature to market.
 *
 * Screenshot slots are declared but commented out until captures are available.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Ruler,
  Bot,
  Layers,
  FileText,
  Calculator,
  DollarSign,
  Upload,
  Crosshair,
  Square,
  MousePointer2,
  Box,
  Package,
  Keyboard,
  Briefcase,
  Files,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Takeoffs & Blueprint Measurement",
  title: "Measure off the plans.",
  titleHighlight: "Quantities flow into the estimate.",
  description:
    "Upload blueprints, calibrate the scale, and draw area, linear, count, and volume measurements directly on the page. Hit Populate Builder and every measurement becomes a priced line item in your estimate — no re-entry, no spreadsheet exports, no switching tools.",

  // heroScreenshotPath: "/screenshots/takeoffs-canvas.png",
  // heroScreenshotAlt: "Blueprint measurement canvas with area overlays, scale bar, and measurements sidebar",

  // ── BENTO GRID ──────────────────────────────────────────────────────────

  featureGrid: {
    title:
      "Everything a takeoff tool should be — without a separate takeoff tool",
    items: [
      {
        icon: Upload,
        title: "PDF & Image Upload",
        description:
          "Drop in blueprints, site plans, or marked-up images. Multi-page plan sets supported — navigate sheets without leaving the workspace.",
      },
      {
        icon: Crosshair,
        title: "Per-Page Scale Calibration",
        description:
          "Click two points of a known dimension, type the real length, and the page is calibrated. Different sheets can have different scales.",
      },
      {
        icon: Square,
        title: "Area Measurements",
        description:
          "Click around a room, a patio, a parking lot. Automatic square footage with support for cutouts and holes.",
      },
      {
        icon: Ruler,
        title: "Linear Measurements",
        description:
          "Trace walls, curbs, pipe runs, fencing, trim. Segments sum to total linear feet as you draw.",
      },
      {
        icon: MousePointer2,
        title: "Count Measurements",
        description:
          "Click each fixture, outlet, door, tree, or rooftop unit. Running count tallied live.",
      },
      {
        icon: Box,
        title: "Volume Measurements",
        description:
          "Draw an area, set a height, and get cubic yards for concrete pours, fill dirt, excavation, or mulch.",
      },
      {
        icon: Layers,
        title: "Work Areas & Phases",
        description:
          "Group measurements by work area (front yard, Level 2, exterior) and assign phases so structure carries through to the estimate.",
      },
      {
        icon: Keyboard,
        title: "Quick Entry Panel",
        description:
          "Typing in quantities is faster than drawing some days. Add measurements by hand without ever touching the canvas.",
      },
      {
        icon: Package,
        title: "Work Package Expansion",
        description:
          "Apply a work package to a measurement and it expands into materials, labor, and equipment line items — production rates and waste factors built in.",
      },
    ],
  },

  // ── WORKFLOW STORY ──────────────────────────────────────────────────────

  workflow: {
    title: "What a takeoff actually looks like in CC",
    description:
      "Takeoffs aren't a separate tool you have to learn — they're a tab on your estimate. Upload, calibrate, measure, populate. Here's the order of operations.",
    steps: [
      {
        label: "Upload",
        title: "Drop in the plan set",
        description:
          "Upload PDFs or images directly into the Takeoff tab of your estimate. Multi-page blueprint sets are supported — navigate between Sheet 1 (site plan), Sheet 3 (floor plan), and Sheet 7 (details) from a single viewer.",
        details: [
          "PDF and image upload (drag and drop)",
          "Multi-page plan sets with page navigator",
          "Smooth blueprint viewer with zoom and pan",
          "Plan set stays linked to the estimate — no file hunting later",
        ],
        // screenshotPath: "/screenshots/takeoffs-upload.png",
      },
      {
        label: "Calibrate",
        title: "Set the scale — per page",
        description:
          "Click two points on a known dimension (a wall, a property line, the scale bar itself) and type the real-world length. The page is calibrated. Different sheets in the same set can have different scales, so calibration is tracked per page.",
        details: [
          "Two-click scale calibration — pick points, type length",
          "Per-page scale tracking for mixed plan sets",
          "Recalibrate any time without losing measurements",
          "Scale preview shows what the canvas will measure",
        ],
        // screenshotPath: "/screenshots/takeoffs-calibrate.png",
      },
      {
        label: "Measure",
        title: "Draw what you're pricing",
        description:
          "Pick a measurement type and draw directly on the blueprint. Area for surfaces, linear for runs, count for fixtures, volume for anything that needs a depth. Organize measurements into work areas as you go so structure flows into the estimate. Prefer typing numbers? The Quick Entry panel lets you skip the canvas entirely.",
        details: [
          "Four measurement types: area (SF/SY), linear (LF), count (EA), volume (CY)",
          "Real-time geometry — quantities update as you draw",
          "Measurements sidebar with edit, rename, reassign",
          "Quick Entry panel for measurements without drawing",
          "Cross-page volume measurements carry height from the source page",
        ],
        // screenshotPath: "/screenshots/takeoffs-draw.png",
      },
      {
        label: "Populate",
        title: "One click from measurements to line items",
        description:
          "Hit Populate Builder and every measurement becomes a line item in the Builder tab, organized by the same work areas you drew on the blueprint. Apply work packages to expand bundled assemblies into detailed material, labor, and equipment lines. Existing line items are preserved — you choose overwrite or append, so nothing gets silently replaced.",
        details: [
          "Measurements → line items in one step",
          "Work areas and phases carry over so the estimate inherits the structure",
          "Work packages expand into detailed material + labor + equipment lines",
          "Overwrite or append — existing line items never silently replaced",
          "Populate confirmation dialog before anything is committed",
        ],
        // screenshotPath: "/screenshots/takeoffs-populate.png",
      },
    ],
  },

  // ── CAPABILITIES ────────────────────────────────────────────────────────
  // Measurement Types and Plan Sets are now separate deep-dive pages.
  // Keeping Work Packages and Work Areas as the hub-level capability cards.

  capabilities: [
    {
      icon: Package,
      title: "Work Packages — Priced Assemblies Out Of The Box",
      description:
        "A work package is a reusable bundle of materials, labor, and equipment. A landscaper builds a 'Patio Install' package once — pavers, base, sand, install labor hours, plate compactor — and applies it to any measurement on any takeoff. The package expands into line items automatically, with your production rates and waste factors already baked in. Build packages per trade, per job type, per division.",
      bullets: [
        "Bundle materials + labor + equipment into a single reusable package",
        "Production rates and waste factors stored with the package",
        "Apply to any measurement on any takeoff — the bundle expands into line items",
        "Track package usage across estimates so templates stay current",
        "Full builder form — add, remove, reorder items per package",
      ],
    },
    {
      icon: Layers,
      title: "Work Areas & Phases — Structure From Day One",
      description:
        "Professional estimates aren't one long list of line items — they're grouped by where the work happens and when. CC lets you group measurements into work areas (front yard, Level 2, exterior) and assign phases as you draw. When you populate the builder, that structure carries straight through to the estimate. No re-grouping. No re-labeling. No copy-paste.",
      bullets: [
        "Group measurements by work area as you draw",
        "Phase assignment for sequenced jobs",
        "Work areas and phases persist through Populate Builder",
        "Rename, merge, or reassign work areas without losing measurements",
        "Structure shows up on the client-facing proposal too",
      ],
    },
  ],

  // ── DEEP DIVES ──────────────────────────────────────────────────────────

  deepDives: {
    title: "Go deeper on the takeoff tool",
    description:
      "Each part of the takeoff workflow has its own dedicated page with full detail.",
    items: [
      {
        icon: Upload,
        title: "Blueprint Editor",
        description:
          "Upload PDFs and images, navigate multi-page plan sets, zoom and pan with hardware-accelerated rendering.",
        href: "/features/takeoffs/blueprint-editor",
      },
      {
        icon: Ruler,
        title: "Measurement Tool",
        description:
          "Four measurement types (area, linear, count, volume), scale calibration, quick entry panel, editable stored geometry.",
        href: "/features/takeoffs/measurement-tool",
      },
      {
        icon: Files,
        title: "Plan Sets",
        description:
          "Multi-page plan set support with per-page scale, cross-page volume measurements, and a sidebar across the full set.",
        href: "/features/takeoffs/plan-sets",
      },
    ],
  },

  // ── CONNECTIONS ─────────────────────────────────────────────────────────

  connections: {
    title: "How takeoffs connect to the rest of the platform",
    items: [
      {
        icon: Calculator,
        flow: "Takeoff → Estimate Builder",
        description:
          "Populate Builder converts every confirmed measurement to a line item in the estimate. Work areas and phases carry over. Existing line items are preserved — you choose overwrite or append.",
      },
      {
        icon: Package,
        flow: "Work Packages → Measurements",
        description:
          "Apply a work package to a measurement and the assembly expands into materials, labor, and equipment line items — production rates and waste factors included.",
      },
      {
        icon: DollarSign,
        flow: "Cost Database → Line Items",
        description:
          "When measurements populate into line items, they draw from your cost database for unit costs, labor rates, and equipment rates. Update your catalog once; every future takeoff benefits.",
      },
      {
        icon: FileText,
        flow: "Measurements → Proposal",
        description:
          "The work area structure you drew on the blueprint flows through the estimate into the client-facing proposal. One takeoff, one estimate, one proposal — no re-organization.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Estimates & Proposals",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Cost Database & Work Packages",
      href: "/features/cost-database",
      icon: DollarSign,
    },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: Bot,
    },
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: FileText,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
  ],
};

export function TakeoffsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
